import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import ExtractorInput from './ExtractorInput';
import FaceFusionLab from './FaceFusionLab';
import FingerExtractor from './FingerExtractor';
import IrisExtractor from './IrisExtractor';
import MatcherInput from './MatcherInput';
import FingerMatcher from './FingerMatcher';
import IrisMatcher from './IrisMatcher';
import QualityChecker from './QualityChecker';
import FingerQuality from './FingerQuality';
import IrisQuality from './IrisQuality';
import { Eye, Fingerprint, User, Scan, GitCompare, Gauge } from 'lucide-react';
import {
  getStoredIsoNamespace,
  setStoredIsoNamespace,
  MODALITY_FACE,
  MODALITY_FINGER,
  MODALITY_IRIS,
  NAV_SECTION_EXTRACTOR,
  NAV_SECTION_MATCHER,
  NAV_SECTION_QUALITY,
  isModalitySupportedForVariant,
  isQualitySupportedForVariant,
  parseModalityFromPath,
  parseNavSectionFromPath,
  firstSupportedRoute,
} from './biosdkRoutes';
import ApiVariantSelector from './components/ApiVariantSelector';
import './App.css';

const MODALITY_NAV_LINKS = {
  [NAV_SECTION_EXTRACTOR]: [
    { modality: MODALITY_FACE, to: '/extractor', text: 'Face', icon: User },
    { modality: MODALITY_FINGER, to: '/extractor/finger', text: 'Finger', icon: Fingerprint },
    { modality: MODALITY_IRIS, to: '/extractor/iris', text: 'Iris', icon: Eye },
  ],
  [NAV_SECTION_MATCHER]: [
    { modality: MODALITY_FACE, to: '/matcher', text: 'Face', icon: User },
    { modality: MODALITY_FINGER, to: '/matcher/finger', text: 'Finger', icon: Fingerprint },
    { modality: MODALITY_IRIS, to: '/matcher/iris', text: 'Iris', icon: Eye },
  ],
  [NAV_SECTION_QUALITY]: [
    { modality: MODALITY_FACE, to: '/quality', text: 'Face', icon: User },
    { modality: MODALITY_FINGER, to: '/quality/finger', text: 'Finger', icon: Fingerprint },
    { modality: MODALITY_IRIS, to: '/quality/iris', text: 'Iris', icon: Eye },
  ],
};

function ContainerNav({ apiVariant }) {
  const location = useLocation();
  const currentPath = location.pathname;
  const section = parseNavSectionFromPath(currentPath);

  const links = useMemo(() => {
    if (!section) return [];
    return (MODALITY_NAV_LINKS[section] || []).filter((link) =>
      isModalitySupportedForVariant(apiVariant, link.modality)
    );
  }, [section, apiVariant]);

  if (!links.length) return null;

  return (
    <div className="container-nav">
      {links.map((link) => {
        const Icon = link.icon;
        const isSelected =
          currentPath === link.to
          || (link.to === '/extractor/finger' && currentPath.startsWith('/extractor/finger'));
        return (
          <Link
            key={link.to}
            to={link.to}
            className={`nav-tab ${isSelected ? 'selected' : ''}`}
          >
            <Icon className="nav-icon" size={18} />
            <span>{link.text}</span>
          </Link>
        );
      })}
    </div>
  );
}

function DefaultMessage() {
  const navigate = useNavigate();

  const handleFeatureClick = (path) => {
    navigate(path);
  };

  return (
    <div className="default-message">
      <div className="welcome-card">
        <img src="/logo.png" alt="Ooru BioCHQ" className="welcome-logo" />
        <h2>Welcome to Ooru BioCHQ</h2>
        <p>Select a category to get started.</p>
        <div className="feature-grid">
          <div className="feature-item clickable" onClick={() => handleFeatureClick('/extractor')}>
            <Scan className="feature-icon" />
            <h3>Extractor</h3>
            <p>Extract biometric templates from images</p>
          </div>
          <div className="feature-item clickable" onClick={() => handleFeatureClick('/matcher')}>
            <GitCompare className="feature-icon" />
            <h3>Matcher</h3>
            <p>Compare biometric samples for verification</p>
          </div>
          <div className="feature-item clickable" onClick={() => handleFeatureClick('/quality')}>
            <Gauge className="feature-icon" />
            <h3>Quality</h3>
            <p>Analyze biometric image quality</p>
          </div>
        </div>
        <div className="modality-badges">
          <span className="modality-badge face">
            <User size={14} /> Face
          </span>
          <span className="modality-badge finger">
            <Fingerprint size={14} /> Finger
          </span>
          <span className="modality-badge iris">
            <Eye size={14} /> Iris
          </span>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [apiVariant, setApiVariant] = useState(getStoredIsoNamespace());
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const qualitySupported = isQualitySupportedForVariant(apiVariant);

  const getNavClass = (path) => {
    if (currentPath.startsWith(path)) return 'nav-link active';
    return 'nav-link';
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleApiVariantChange = (selected) => {
    setApiVariant(setStoredIsoNamespace(selected));
  };

  useEffect(() => {
    const section = parseNavSectionFromPath(location.pathname);
    if (!section) return;

    if (section === NAV_SECTION_QUALITY && !isQualitySupportedForVariant(apiVariant)) {
      const fallback = firstSupportedRoute(NAV_SECTION_EXTRACTOR, apiVariant) || '/';
      if (location.pathname !== fallback) {
        navigate(fallback, { replace: true });
      }
      return;
    }

    const modality = parseModalityFromPath(location.pathname);
    if (!isModalitySupportedForVariant(apiVariant, modality)) {
      const fallback = firstSupportedRoute(section, apiVariant);
      if (!fallback) {
        navigate('/', { replace: true });
        return;
      }
      if (location.pathname !== fallback) {
        const preserveSearch = section === NAV_SECTION_EXTRACTOR
          && fallback === '/extractor/finger'
          && location.search;
        navigate(`${fallback}${preserveSearch || ''}`, { replace: true });
      }
    }
  }, [apiVariant, location.pathname, location.search, navigate]);

  return (
    <div className="App">
      <header className="header">
        <div className="header-top">
          <div className="logo-section" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
            <img src="/logo.png" alt="Ooru BioCHQ" className="logo-icon" />
            <h1 className="service-name">Ooru BioCHQ</h1>
          </div>
          <span className="version-badge">v1.0</span>
        </div>
      </header>
      <div className="api-variant-bar">
        <ApiVariantSelector value={apiVariant} onChange={handleApiVariantChange} compact />
      </div>

      <nav className="main-nav">
        <div className="nav-links">
          <Link to="/extractor" className={getNavClass('/extractor')}>
            <Scan size={18} />
            <span>Extractor</span>
          </Link>
          <Link to="/matcher" className={getNavClass('/matcher')}>
            <GitCompare size={18} />
            <span>Matcher</span>
          </Link>
          {qualitySupported && (
            <Link to="/quality" className={getNavClass('/quality')}>
              <Gauge size={18} />
              <span>Quality</span>
            </Link>
          )}
        </div>
      </nav>

      <div className="main-container">
        <ContainerNav apiVariant={apiVariant} />

        <Routes>
          {/* Default Route */}
          <Route path="/" element={<DefaultMessage />} />
          
          {/* Extractor Routes */}
          <Route path="/extractor" element={<ExtractorInput apiVariant={apiVariant} />} />
          <Route path="/extractor/face/fusion-lab" element={<FaceFusionLab apiVariant={apiVariant} />} />
          <Route path="/extractor/finger" element={<FingerExtractor apiVariant={apiVariant} />} />
          <Route path="/extractor/finger/slap" element={<Navigate to="/extractor/finger?mode=slap" replace />} />
          <Route path="/extractor/iris" element={<IrisExtractor apiVariant={apiVariant} />} />
          
          {/* Matcher Routes */}
          <Route path="/matcher" element={<MatcherInput apiVariant={apiVariant} />} />
          <Route path="/matcher/finger" element={<FingerMatcher apiVariant={apiVariant} />} />
          <Route path="/matcher/iris" element={<IrisMatcher apiVariant={apiVariant} />} />

          {/* Quality Routes */}
          <Route path="/quality" element={<QualityChecker apiVariant={apiVariant} />} />
          <Route path="/quality/finger" element={<FingerQuality apiVariant={apiVariant} />} />
          <Route path="/quality/iris" element={<IrisQuality apiVariant={apiVariant} />} />

          {/* Fallback Route */}
          <Route path="*" element={<DefaultMessage />} />
        </Routes>
      </div>
    </div>
  );
}

function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export { App };
export default AppWrapper;
